import {useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {useSelector} from 'react-redux';
import {Alert, Button, Card, Checkbox, Input, Tag, Typography} from 'antd';
import moment from 'moment';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import {makeSelectTabOperations} from 'redux/store/auth';
import {unwrap} from 'pages/kybDashboard/kybUtils';
import {
  KYC_HUB_COMPLIANCE_RISK,
  SET_KYC_HUB_COMPLIANCE_RISK,
} from './resolvers';
import {
  RISK_LEVELS,
  RISK_TAGS,
  SUBJECT_PERMISSION_TAB,
  riskLevelLabel,
  riskTagLabel,
} from './kycHubUtils';
import styles from './kycHub.module.scss';

const {Text} = Typography;

const REFETCH = ['KycHubComplianceRisk', 'KycHubActivity'];
const MAX_REASON = 1000;

/**
 * An admin's compliance risk rating and tags for one subject.
 *
 * Entered by hand; it does not touch KYC, AML, document-check or KYB statuses.
 * `subjectId` is the user ID for `INDIVIDUAL` and the institutional account ID
 * for `INSTITUTIONAL`. Every set, change or clear needs a reason, and each one
 * lands in the Hub's activity timeline.
 */
export const ComplianceRiskCard = ({subjectType, subjectId}) => {
  const operations = useSelector(state =>
    makeSelectTabOperations(state, SUBJECT_PERMISSION_TAB[subjectType]),
  );
  const canView = Boolean(operations?.view);
  const canUpdate = Boolean(operations?.update);
  const subject = {subjectType, subjectId};

  // 'set' edits the rating and tags; 'clear' removes the assessment.
  const [mode, setMode] = useState(null);
  const [form, setForm] = useState({level: null, tags: [], reason: ''});
  const [touched, setTouched] = useState(false);

  const {data, loading, error} = useQuery(KYC_HUB_COMPLIANCE_RISK, {
    variables: {input: subject},
    skip: !canView || !subjectId,
    fetchPolicy: 'cache-and-network',
  });
  const read = unwrap(data?.kycHubComplianceRisk);
  const risk = read.data;
  const loadError = read.error || (error ? 'Could not load the risk.' : null);

  const [save, {loading: saving}] = useMutation(SET_KYC_HUB_COMPLIANCE_RISK);

  const open = next => {
    setMode(next);
    setTouched(false);
    // The reason explains this change, so it starts empty every time.
    setForm({
      level: next === 'set' ? risk?.level || null : null,
      tags: next === 'set' ? risk?.tags || [] : [],
      reason: '',
    });
  };

  const reasonMissing = !form.reason.trim();
  const levelMissing = mode === 'set' && !form.level;

  const submit = async () => {
    setTouched(true);
    if (reasonMissing || levelMissing) return;
    try {
      const {data: result} = await save({
        variables: {
          input: {
            ...subject,
            level: mode === 'clear' ? null : form.level,
            tags: mode === 'clear' ? [] : form.tags,
            reason: form.reason.trim(),
          },
        },
        // Only a change refreshes the card and the timeline; an Error leaves
        // both as they were.
        refetchQueries: ({data: out}) =>
          out?.setKycHubComplianceRisk?.__typename === 'Error' ? [] : REFETCH,
        awaitRefetchQueries: true,
      });
      const {error: saveError} = unwrap(result?.setKycHubComplianceRisk);
      // The form keeps its values, so the admin can adjust and retry.
      if (saveError) return toast.error(saveError);
      toast.success(
        mode === 'clear'
          ? 'Compliance risk cleared.'
          : 'Compliance risk saved.',
      );
      setMode(null);
    } catch (err) {
      toast.error('The request failed. Try again.');
    }
  };

  const level = RISK_LEVELS.find(item => item.value === risk?.level);

  return (
    <Card
      title="Compliance risk"
      className={styles.card}
      extra={
        canView &&
        canUpdate &&
        !loading && (
          <div className={styles.rowActions}>
            {risk && (
              <Button size="small" danger onClick={() => open('clear')}>
                Clear
              </Button>
            )}
            <Button size="small" type="primary" onClick={() => open('set')}>
              {risk ? 'Change' : 'Set rating'}
            </Button>
          </div>
        )
      }>
      {!canView ? (
        <Alert
          type="info"
          showIcon
          message={`You need view access to ${
            subjectType === 'INSTITUTIONAL'
              ? 'Business Verification'
              : 'the KYC Dashboard'
          } to see the compliance risk.`}
        />
      ) : loadError ? (
        <Alert type="error" showIcon message={loadError} />
      ) : loading && !data ? (
        <Text type="secondary">Loading…</Text>
      ) : !risk ? (
        <div className={styles.riskEmpty}>
          <Tag>Not assessed</Tag>
          <Text type="secondary">
            No admin has rated this{' '}
            {subjectType === 'INSTITUTIONAL' ? 'business' : 'user'} yet.
          </Text>
        </div>
      ) : (
        <div className={styles.risk}>
          <div className={styles.riskHead}>
            {risk.level ? (
              <Tag color={level?.color}>{riskLevelLabel(risk.level)} risk</Tag>
            ) : (
              <Tag>No rating</Tag>
            )}
            {(risk.tags || []).map(tag => (
              <Tag key={tag} className={styles.riskTag}>
                {riskTagLabel(tag)}
              </Tag>
            ))}
          </div>
          <dl className={styles.riskMeta}>
            <dt>Reason</dt>
            <dd>{risk.reason || '—'}</dd>
            <dt>Updated</dt>
            <dd>
              {risk.updatedAt
                ? moment(risk.updatedAt).format('DD MMM YYYY, HH:mm')
                : '—'}
            </dd>
            <dt>By admin</dt>
            <dd className={styles.riskId}>{risk.updatedBy || '—'}</dd>
          </dl>
        </div>
      )}

      <DBModal
        isOpen={Boolean(mode)}
        handleClose={saving ? undefined : () => setMode(null)}
        width={480}
        destroyOnClose
        content={
          <div className={styles.modalBody}>
            <h2 className={styles.modalTitle}>
              {mode === 'clear'
                ? 'Clear compliance risk'
                : risk
                ? 'Change compliance risk'
                : 'Set compliance risk'}
            </h2>
            <Text type="secondary">
              {mode === 'clear'
                ? 'For an assessment entered in error. The subject goes back to Not assessed; the history stays in Activity.'
                : 'Your own assessment. It does not change any verification status.'}
            </Text>

            {mode === 'set' && (
              <>
                <div className={styles.modalField}>
                  <span className={styles.fieldLabel}>Rating</span>
                  <div className={styles.riskLevels} role="radiogroup">
                    {RISK_LEVELS.map(item => (
                      <button
                        key={item.value}
                        type="button"
                        role="radio"
                        aria-checked={form.level === item.value}
                        className={
                          form.level === item.value
                            ? styles.riskLevelOn
                            : styles.riskLevel
                        }
                        onClick={() => setForm({...form, level: item.value})}>
                        {item.label}
                      </button>
                    ))}
                  </div>
                  {touched && levelMissing && (
                    <Text type="danger" className={styles.fieldError}>
                      Pick a rating.
                    </Text>
                  )}
                </div>

                <div className={styles.modalField}>
                  <span className={styles.fieldLabel}>Tags (optional)</span>
                  <Checkbox.Group
                    className={styles.riskTagChoices}
                    value={form.tags}
                    options={RISK_TAGS}
                    onChange={tags => setForm({...form, tags})}
                  />
                </div>
              </>
            )}

            <div className={styles.modalField}>
              <label className={styles.fieldLabel} htmlFor="kyc-risk-reason">
                Reason (required)
              </label>
              <Input.TextArea
                id="kyc-risk-reason"
                rows={3}
                value={form.reason}
                maxLength={MAX_REASON}
                showCount
                placeholder={
                  mode === 'clear'
                    ? 'e.g. Assessment entered in error'
                    : 'e.g. Manual compliance review'
                }
                onChange={event =>
                  setForm({...form, reason: event.target.value})
                }
              />
              {touched && reasonMissing && (
                <Text type="danger" className={styles.fieldError}>
                  Give a reason.
                </Text>
              )}
            </div>

            <div className={styles.modalActions}>
              <Button onClick={() => setMode(null)} disabled={saving}>
                Cancel
              </Button>
              <Button
                type="primary"
                danger={mode === 'clear'}
                loading={saving}
                disabled={saving}
                onClick={submit}>
                {mode === 'clear' ? 'Clear risk' : 'Save'}
              </Button>
            </div>
          </div>
        }
      />
    </Card>
  );
};
